// Runtime-editable brand palette. Colors are applied as CSS variables that
// Tailwind reads via `rgb(var(--c-x) / <alpha-value>)`, so opacity modifiers
// (e.g. bg-sage/12) keep working.

export const DEFAULT_THEME = {
  sage: "#9CB099",
  sageDark: "#7A8F77",
  ocean: "#8B9DAA",
  cream: "#FBF9F6",
  cream2: "#F0EDE6",
  sand: "#E5DFD5",
  ink: "#1A1D1A",
  inkSoft: "#4A4F48",
  inkMuted: "#757A74",
  peach: "#E8B4A6",
  peachLight: "#F5D6CD",
};

// theme key -> CSS variable name (matches tailwind.config.js)
export const THEME_VARS = {
  sage: "--c-sage",
  sageDark: "--c-sage-dark",
  ocean: "--c-ocean",
  cream: "--c-cream",
  cream2: "--c-cream-2",
  sand: "--c-sand",
  ink: "--c-ink",
  inkSoft: "--c-ink-soft",
  inkMuted: "--c-ink-muted",
  peach: "--c-peach",
  peachLight: "--c-peach-light",
};

export const hexToChannels = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
};

// Returns a valid #rrggbb for <input type="color">, falling back to a default.
export const safeHex = (hex, fallback = "#000000") => {
  if (typeof hex !== "string") return fallback;
  let h = hex.trim();
  if (!h.startsWith("#")) h = `#${h}`;
  if (/^#[0-9a-fA-F]{3}$/.test(h)) {
    h = "#" + h.slice(1).split("").map((c) => c + c).join("");
  }
  return /^#[0-9a-fA-F]{6}$/.test(h) ? h : fallback;
};

export const applyTheme = (theme = {}) => {
  const root = document.documentElement;
  Object.keys(THEME_VARS).forEach((key) => {
    const ch = hexToChannels(theme[key] || DEFAULT_THEME[key]);
    if (ch) root.style.setProperty(THEME_VARS[key], ch);
  });
  const heading = theme.fontHeading || DEFAULT_FONTS.heading;
  const body = theme.fontBody || DEFAULT_FONTS.body;
  root.style.setProperty("--font-heading", `"${heading}"`);
  root.style.setProperty("--font-body", `"${body}"`);
  ensureFontsLoaded(heading, body);
};

// Dynamically load the chosen Google Fonts so any pick from HEADING_FONTS /
// BODY_FONTS actually renders (Google Fonts stylesheet auto-fetches WOFF).
const buildFontsUrl = (families) => {
  const encode = (f) => f.trim().replace(/\s+/g, "+");
  const parts = families
    .filter(Boolean)
    .filter((f, i, arr) => arr.indexOf(f) === i)
    .map((f) => `family=${encode(f)}:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400`);
  if (!parts.length) return null;
  return `https://fonts.googleapis.com/css2?${parts.join("&")}&display=swap`;
};

const ensureFontsLoaded = (heading, body) => {
  const url = buildFontsUrl([heading, body]);
  if (!url) return;
  let link = document.getElementById("dynamic-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "dynamic-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== url) link.href = url;
};

// Supported fonts. `applyTheme` loads whichever ones are selected via Google
// Fonts at runtime, so admin picks always render.
export const DEFAULT_FONTS = { heading: "Cormorant Garamond", body: "Manrope" };

export const HEADING_FONTS = [
  "Cormorant Garamond",
  "Playfair Display",
  "Fraunces",
  "Lora",
  "DM Serif Display",
  "Libre Baskerville",
];

export const BODY_FONTS = [
  "Manrope",
  "Inter",
  "Work Sans",
  "Nunito Sans",
  "Poppins",
  "DM Sans",
  "Karla",
];
